import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { SadmEntity } from '@pixacare/domain';
import { SadmClient } from '@pixacare/dtos';
import { catchError, concatMap, map, mergeMap, Observable } from 'rxjs';
import { SadmHttpService } from 'src/app/services/http/sadm.http.service';
import { lifeCycleActions } from '../life-cycle/life-cycle.actions';
import { notificationsActions } from '../notifications/notifications.actions';
import { sequencesActions } from '../sequences/sequences.actions';
import { sadmActions } from './sadm.actions';

@Injectable()
export class SadmEffects {
  private readonly actions$ = inject(Actions);
  private readonly sadmService = inject(SadmHttpService);
  private readonly translocoService = inject(TranslocoService);

  getSadmClients$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sadmActions.getSadmClients),
      mergeMap(() =>
        this.sadmService.getSadmClients({ forms: true }).pipe(
          concatMap((sadmClients: SadmClient[]) => [
            sadmActions.getSadmClientsSuccess({ sadmClients, override: true }),
            sadmActions.setSadmClientsQuickModules({ sadmClients }),
          ]),
          catchError((error: Error) => [lifeCycleActions.loadError({ error })]),
        ),
      ),
    ),
  );

  getSadmEntities$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sadmActions.getSadmEntities),
      mergeMap(({ clientCode, patientId, isArchived }) =>
        this.sadmService.getSadmEntities({ clientCode, patientId, isArchived }).pipe(
          map((sadmEntities) => sadmActions.getSadmEntitiesSuccess({ clientCode, patientId, sadmEntities })),
          catchError((error: Error) => [lifeCycleActions.loadError({ error })]),
        ),
      ),
    ),
  );

  getSadmEntitiesSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sadmActions.getSadmEntitiesSuccess),
      mergeMap(({ sadmEntities }) =>
        sadmEntities
          .filter((sadmEntity) => !!sadmEntity.sequences?.length)
          .map((sadmEntity) =>
            sequencesActions.loadSequences({
              clientCode: sadmEntity.clientCode,
              data: sadmEntity.sequences,
            }),
          ),
      ),
    ),
  );

  getSadmEntity$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sadmActions.getSadmEntity),
      mergeMap(({ sadmEntityId }) =>
        this.sadmService
          .getSadmEntity(sadmEntityId, {
            includeSadmClient: true,
          })
          .pipe(
            concatMap((sadmEntity) => [
              ...(sadmEntity.sadmClient
                ? [
                    sadmActions.getSadmClientsSuccess({
                      sadmClients: [sadmEntity.sadmClient],
                      override: false,
                    }),
                  ]
                : []),
              sadmActions.getSadmEntitySuccess({ sadmEntity }),
            ]),
            catchError((error: Error) => [lifeCycleActions.loadError({ error })]),
          ),
      ),
    ),
  );

  mergeSadmEntity$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sadmActions.mergeSadmEntity),
      mergeMap(({ fromSadmEntityId, toSadmEntityId }) =>
        this.sadmService.mergeSadmEntity(fromSadmEntityId, toSadmEntityId).pipe(
          mergeMap(() => [
            sadmActions.unloadSadmEntity({ sadmEntityId: fromSadmEntityId }),
            notificationsActions.success({
              title: this.translocoService.translate('La fusion des lésions a été effectuée'),
              message: this.translocoService.translate('La lésion a été supprimée'),
            }),
          ]),
          catchError((error: Error) => [
            lifeCycleActions.loadError({ error }),
            notificationsActions.error({
              error,
              title: this.translocoService.translate(`Une erreur est survenue.`),
              message: this.translocoService.translate("Nous n'avons pas pu fusionner les lésions."),
            }),
          ]),
        ),
      ),
    ),
  );

  deleteSadmEntity$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sadmActions.deleteSadmEntity),
      mergeMap(({ sadmEntityId }) =>
        this.sadmService.deleteSadmEntity(sadmEntityId).pipe(
          mergeMap(() => [
            sadmActions.unloadSadmEntity({ sadmEntityId }),
            sequencesActions.deleteSadmEntityFromSequences({
              sadmEntityId,
            }),
            notificationsActions.success({
              title: this.translocoService.translate('La lésion a été supprimée'),
              message: this.translocoService.translate('La lésion a été supprimée'),
            }),
          ]),
          catchError((error: Error) => [
            lifeCycleActions.loadError({ error }),
            notificationsActions.error({
              error,
              title: this.translocoService.translate(`Une erreur est survenue.`),
              message: this.translocoService.translate("Nous n'avons pas pu supprimer la lésion."),
            }),
          ]),
        ),
      ),
    ),
  );

  updateSadmEntity$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sadmActions.updateSadmEntity),
      mergeMap(({ sadmEntityId, updateSadmEntityRequest: { name, entityCreationFormAnswer } }) =>
        this.sadmService.updateSadmEntity(sadmEntityId, { name, entityCreationFormAnswer }).pipe(
          mergeMap((updatedSadmEntity: SadmEntity) => [
            sadmActions.updateSadmEntitySuccess({ sadmEntity: updatedSadmEntity }),
            notificationsActions.success({
              title: this.translocoService.translate('La lésion a bien été modifiée.'),
              message: this.translocoService.translate('Lésion modifiée'),
            }),
          ]),
          catchError((error: Error) => [
            lifeCycleActions.loadError({ error }),
            notificationsActions.error({
              error,
              title: this.translocoService.translate(`Une erreur est survenue.`),
              message: this.translocoService.translate("Nous n'avons pas pu modifier la lésion."),
            }),
          ]),
        ),
      ),
    ),
  );

  archiveSadmEntity$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sadmActions.archiveSadmEntity),
      mergeMap(({ sadmEntityId }) =>
        this.sadmService.archive(sadmEntityId).pipe(
          mergeMap((archivedSadmEntity: SadmEntity) => [
            sadmActions.archiveSadmEntitySuccess({ sadmEntity: archivedSadmEntity }),
            notificationsActions.success({
              title: this.translocoService.translate('La lésion a bien été archivée.'),
              message: this.translocoService.translate('Lésion archivée'),
            }),
          ]),
          catchError((error: Error) => [
            lifeCycleActions.loadError({ error }),
            notificationsActions.error({
              error,
              title: this.translocoService.translate(`Une erreur est survenue.`),
              message: this.translocoService.translate("Nous n'avons pas pu archiver la lésion."),
            }),
          ]),
        ),
      ),
    ),
  );

  unarchiveSadmEntity$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sadmActions.unarchiveSadmEntity),
      mergeMap(({ sadmEntityId }) =>
        this.sadmService.unarchive(sadmEntityId).pipe(
          mergeMap((unarchivedSadmEntity: SadmEntity) => [
            sadmActions.unarchiveSadmEntitySuccess({ sadmEntity: unarchivedSadmEntity }),
            notificationsActions.success({
              title: this.translocoService.translate('La lésion a bien été désarchivée.'),
              message: this.translocoService.translate('Lésion désarchivée'),
            }),
          ]),
          catchError((error: Error) => [
            lifeCycleActions.loadError({ error }),
            notificationsActions.error({
              error,
              title: this.translocoService.translate(`Une erreur est survenue.`),
              message: this.translocoService.translate("Nous n'avons pas pu désarchiver la lésion."),
            }),
          ]),
        ),
      ),
    ),
  );
}
